import ApplicationController from "./application_controller"
import Logger from '../lib/logger'

/**
 * FilterableListController - Generic client-side list filtering
 * 
 * Replaces: table_search, user_search, member_search
 * 
 * Configuration:
 * - data-filterable-list-search-by-value: Array of attributes to search (e.g. ["textContent", "data-name"])
 * - data-filterable-list-debounce-wait-value: Milliseconds to wait before filtering (default: 150)
 * - data-filterable-list-animation-enabled-value: Whether to show fadeInUp animation (default: true)
 * - data-filterable-list-min-length-value: Minimum characters to trigger search (default: 1)
 */
export default class extends ApplicationController {
  static targets = ["input", "item", "emptyState", "list"]
  static values = {
    searchBy: { type: Array, default: ['textContent'] },
    debounceWait: { type: Number, default: 150 },
    animationEnabled: { type: Boolean, default: true },
    minLength: { type: Number, default: 1 }
  }

  connect() {
    super.connect()
    // Bind the filter method to this instance and wrap it in a debounce
    this.debouncedFilter = this.debounce(this.filterLogic.bind(this), this.debounceWaitValue)
  }

  // Action method called by events (e.g. input->filterable-list#handleInput)
  handleInput(event) {
    this.debouncedFilter(event)
  }

  // Core filter logic
  filterLogic(event) {
    const query = (this.hasInputTarget ? this.inputTarget.value : event?.target?.value || "").toLowerCase().trim()
    
    // Only filter if query is empty (to reset) or meets min length
    if (query.length > 0 && query.length < this.minLengthValue) return

    let visibleCount = 0
    
    // Optional pulse effect for search icons
    const icon = this.element.querySelector('.fa-search')
    if (icon) {
      icon.classList.add('animate-pulse')
      setTimeout(() => icon.classList.remove('animate-pulse'), 500)
    }

    this.itemTargets.forEach(item => {
      const isVisible = this.matchItem(item, query)
      
      if (isVisible) {
        item.classList.remove("d-none")
        visibleCount++
        
        if (this.animationEnabledValue) {
          item.style.animation = `fadeInUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards ${visibleCount * 0.03}s`
        }
      } else {
        item.classList.add("d-none")
        if (this.animationEnabledValue) {
          item.style.animation = 'none'
        }
      }
    })

    this.updateEmptyState(visibleCount, query)
    
    Logger.debug('List filtered', { query, visibleCount, total: this.itemTargets.length })
  }

  matchItem(item, query) {
    if (query === "") return true

    return this.searchByValue.some(attr => {
      let text = ""
      if (attr === 'textContent') {
        text = item.textContent
      } else if (attr.startsWith('data-')) {
        // Convert data-attribute-name to datasetProperty (camelCase)
        const dataKey = attr.replace('data-', '').replace(/-([a-z])/g, (g) => g[1].toUpperCase())
        text = item.dataset[dataKey] || ""
      }
      return text.toLowerCase().includes(query)
    })
  }

  updateEmptyState(count, query) {
    if (this.hasEmptyStateTarget) {
      if (count === 0 && query !== "") {
        this.emptyStateTarget.classList.remove("d-none")
        if (this.animationEnabledValue) {
          this.emptyStateTarget.style.animation = 'fadeIn 0.3s ease-out forwards'
        }
      } else {
        this.emptyStateTarget.classList.add("d-none")
      }
    }
  }

  clear() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.filterLogic()
    }
  }
}
